import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RevenueDrivers from './components/RevenueDrivers';
import ExecutionDrivers from './components/ExecutionDrivers';
import ResourceDrivers from './components/ResourceDrivers';
import CostDrivers from './components/CostDrivers';
import ProfitDrivers from './components/ProfitDrivers';
import EvidenceLayer from './components/EvidenceLayer';
import { exportToPDF } from '../../services/exportService';
import { trackFeatureUsage } from '../../services/analyticsService';

const DrillDownAnalysis = () => {
  const [activeTab, setActiveTab] = useState('revenue');
  const [showEvidence, setShowEvidence] = useState(false);
  const [evidenceContext, setEvidenceContext] = useState({ type: '', name: '' });

  const tabs = [
    { id: 'revenue', label: 'Revenue Drivers', icon: 'DollarSign' },
    { id: 'execution', label: 'Execution & Delivery', icon: 'Activity' },
    { id: 'resources', label: 'People & Resources', icon: 'Users' },
    { id: 'costs', label: 'Cost Drivers', icon: 'Wallet' },
    { id: 'profit', label: 'Profit Drivers', icon: 'TrendingUp' }
  ];

  const handleShowEvidence = (type, name) => {
    setEvidenceContext({ type, name });
    setShowEvidence(true);
  };

  const handleBackToDrivers = () => {
    setShowEvidence(false);
    setEvidenceContext({ type: '', name: '' });
  };

  const renderContent = () => {
    if (showEvidence) {
      return <EvidenceLayer driverType={evidenceContext?.type} driverName={evidenceContext?.name} onBackToDrivers={handleBackToDrivers} />;
    }

    switch (activeTab) {
      case 'revenue':
        return <RevenueDrivers onShowEvidence={handleShowEvidence} />;
      case 'execution':
        return <ExecutionDrivers onShowEvidence={handleShowEvidence} />;
      case 'resources':
        return <ResourceDrivers onShowEvidence={handleShowEvidence} />;
      case 'costs':
        return <CostDrivers onShowEvidence={handleShowEvidence} />;
      case 'profit':
        return <ProfitDrivers onShowEvidence={handleShowEvidence} />;
      default:
        return <RevenueDrivers onShowEvidence={handleShowEvidence} />;
    }
  };

  const handleExportPDF = async () => {
    trackFeatureUsage('drill_down_pdf_export', { activeTab });
    
    const tabTitles = {
      revenue: 'Revenue Drivers Analysis',
      execution: 'Execution & Delivery Analysis',
      resources: 'People & Resources Analysis',
      costs: 'Cost Drivers Analysis',
      profit: 'Profit Drivers Analysis'
    };

    const result = await exportToPDF({
      title: tabTitles?.[activeTab] || 'Drill Down Analysis',
      data: {
        sectionTitle: tabTitles?.[activeTab],
        description: `Comprehensive ${activeTab} analysis and performance tracking`,
        tables: [] // Component-specific data would be added here
      },
      type: 'drill-down'
    });
    
    if (result?.success) {
      console.log('PDF exported successfully:', result?.fileName);
    } else {
      console.error('PDF export failed:', result?.error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <Breadcrumb />
        <main className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-[1600px] mx-auto space-y-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
                    {showEvidence ? 'Evidence Layer' : 'Data Drivers'}
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground font-caption">
                    {showEvidence 
                      ? 'Detailed transaction-level evidence supporting driver metrics' :'Comprehensive business performance analysis across key operational drivers'
                    }
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {showEvidence && (
                    <Button
                      onClick={handleBackToDrivers}
                      variant="outline"
                      size="sm"
                      iconName="ArrowLeft"
                    >
                      Back to Drivers
                    </Button>
                  )}
                  <Button
                    onClick={handleExportPDF}
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    className="hidden sm:flex"
                  >
                    Export PDF
                  </Button>
                </div>
              </div>

              {!showEvidence && (
                <div className="bg-card rounded-lg border border-border p-2 overflow-x-auto">
                  <div className="flex gap-2 min-w-max">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-body font-medium transition-smooth hover-lift whitespace-nowrap ${
                          activeTab === tab?.id
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        <Icon name={tab?.icon} size={18} />
                        <span className="text-sm">{tab?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              {renderContent()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DrillDownAnalysis;